import { yupResolver } from '@hookform/resolvers/yup';
import { pageIntroContent } from '@insify/css/classes.module.css';
import { Input, IntlPhoneInput } from '@insify/design-system/components';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { RichContentWithTooltip } from '../../components/RichContent';
import { WizardLayoutFormWithCommonSection } from '../../components/WizardLayoutForm';

import * as styles from './Representative.module.css';
import { normalizeRepresentative } from './utils';
import { validation } from './validation';

import type { RepresentativeFormType, RepresentativeProps } from './types';

export const Representative = ({
  disableCompanyName,
  formData,
  eventHandlers,
  content,
  translations,
  AddressForm,
  addressValidation,
  isSubmitting: isSubmittingFromProps,
  defaultCountry = 'nl',
}: RepresentativeProps): JSX.Element => {
  const formMethods = useForm<RepresentativeFormType>({
    resolver: yupResolver(validation(translations, disableCompanyName).concat(addressValidation)),
    shouldUnregister: true,
    defaultValues: formData,
    mode: 'onSubmit',
  });

  const {
    register,
    handleSubmit,
    control,
    formState: {
      errors: { representative: representativeErrors, companyName: companyNameErrors },
      isSubmitting: isSubmittingFromForm,
    },
  } = formMethods;

  const isSubmitting = isSubmittingFromProps || isSubmittingFromForm;

  const onSubmit = (data: RepresentativeFormType) => {
    data.representative = normalizeRepresentative(data.representative);
    eventHandlers.onSubmit(data);
  };

  return (
    <FormProvider {...formMethods}>
      <WizardLayoutFormWithCommonSection
        isSubmitting={isSubmitting}
        backCopy={translations.back}
        onBack={eventHandlers.onBack}
        forwardCopy={translations.forward}
        onSubmit={handleSubmit(onSubmit, eventHandlers.onSubmitError)}
      >
        <RichContentWithTooltip content={content} className={pageIntroContent} />
        <Input
          id='companyName'
          trackingId='companyName'
          error={companyNameErrors?.message}
          {...register('companyName')}
          autoComplete='organization'
          disabled={isSubmitting || disableCompanyName}
          label={translations.companyName}
          tooltip={translations.companyNameTooltip}
        />
        <Input
          id='firstName'
          trackingId='firstName'
          error={representativeErrors?.firstName?.message}
          {...register('representative.firstName')}
          autoComplete='given-name'
          disabled={isSubmitting}
          label={translations.firstName}
        />
        <Input
          id='lastName'
          trackingId='lastName'
          error={representativeErrors?.lastName?.message}
          {...register('representative.lastName')}
          autoComplete='family-name'
          disabled={isSubmitting}
          label={translations.lastName}
        />
        <Input
          type='email'
          id='email'
          trackingId='email'
          error={representativeErrors?.email?.message}
          {...register('representative.email')}
          autoComplete='email'
          disabled={isSubmitting}
          label={translations.email}
        />
        <Controller
          name='representative.phone'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <IntlPhoneInput
              trackingId='phone'
              id='phone'
              name={field.name}
              value={field.value}
              onChange={field.onChange}
              ref={field.ref}
              error={error?.message}
              placeholder='+31208881089'
              label={translations.phone}
              defaultCountry={defaultCountry}
              preferredCountries={['nl', 'be', 'de', 'pl', 'gb', 'hu', 'ro']}
              disabled={isSubmitting}
            />
          )}
        />
        <AddressForm className={styles.addressForm} />
      </WizardLayoutFormWithCommonSection>
    </FormProvider>
  );
};
